import GameObject from '../../../GameObject/GameObject';
import AnimationClip from '../AnimationClip';
import { default as TNTSprite } from '../../../Prototype/Level/Card/TNT';
import BaseCard from '../../../Prototype/Level/Card/BaseCard';
import TransformComponent from '../../../Component/Transform';
import { AwaitIterators, PlayAnimation, StartAnimation } from '../AnimationHelper';
import { Easing, Tween } from '@tweenjs/tween.js';
import { delay } from '../../../Utility/Task/TaskQueue';
import Renderable from '../../../Component/Renderable';
import SpriteComponent from '../../../Component/Render/Sprite';
import { PlaySound } from '../../Audio/AudioHelper';
import { AudioTrackType } from '../../Audio/AudioClip';
import CardSystem from '../../Card/CardSystem';
import { showCardScoreEffectAt } from '../CardAnimationHelper';
import Container from '../../../Prototype/Container';
import Scene from '../../../Scene/Scene';
import { UIEvent } from '../../UI/BaseUISystem';
import { CardFold } from './CardFold';
import { RenderSystemEvent } from 'System/Render/RenderSystem';

export class BoosterTNT extends AnimationClip {
    public *execute(
        animator: GameObject,
        meta: {
            system: CardSystem;
            targets: { card: BaseCard; score: number }[];
            flipCard?: string;
        }
    ): IterableIterator<number> {
        meta.system.scene.emit('ToggleLevelHUD', false);

        const cards: { card: BaseCard; tnt: TNTSprite; score: number }[] = [];

        for (let target of meta.targets) {
            animator.addChild(target.card);

            // Create TNT sprites.
            const tnt = GameObject.Instantiate(TNTSprite);
            tnt.getComponent(SpriteComponent).visible = false;
            animator.addChild(tnt);

            cards.push({ card: target.card, tnt: tnt, score: target.score });

            // Ensure that the card is visible.
            target.card.getComponent(Renderable).visible = true;
        }

        if (meta.flipCard) {
            const cardToFold: BaseCard = meta.system.getCardByID(meta.flipCard);
            meta.system.scene.emit(RenderSystemEvent.BRING_TO_FRONT, cardToFold);
            yield* PlayAnimation(new CardFold(cardToFold, { system: meta.system }));
        }

        // Fall animations.
        let fallOffsetIndex = 0;
        const fallAnimations = cards.map(cardTNT => {
            return this.tweenInTNT(cardTNT, fallOffsetIndex++);
        });

        yield* AwaitIterators(fallAnimations);

        // Pinch animations.
        const pinchAnimations = cards.map(cardTNT => {
            const tntTransform = cardTNT.tnt.getComponent(TransformComponent);
            const scaleTween = this.getTween(tntTransform.scale);
            return this.pinch(scaleTween);
        });

        yield* AwaitIterators(pinchAnimations);

        const explodeAnimations = cards.map((cardTNT, index) => {
            return this.explode(animator, cardTNT, index * 150, meta.system.scene);
        });

        meta.system.scene.emit('UpdateScoreProgress');

        yield* AwaitIterators(explodeAnimations);
        meta.system.scene.emit(UIEvent.RESTORE_INTERACTIVITY, true);
    }

    private *tweenInTNT(cardTNT: any, fallOffsetIndex: number): IterableIterator<number> {
        const cardTransform = cardTNT.card.getComponent(TransformComponent);
        const tntTransform = cardTNT.tnt.getComponent(TransformComponent);
        const tntSprite = cardTNT.tnt.getComponent(SpriteComponent);

        // Initial alpha.
        tntSprite.alpha = 0;

        const alphaTween = this.getTween(tntSprite);
        alphaTween.to({ alpha: 1 }, 100).delay(fallOffsetIndex * 100);

        // Set initial TNT position.
        tntTransform.position.set(cardTransform.position.x, cardTransform.position.y - 400);

        cardTNT.tnt.getComponent(SpriteComponent).visible = true;

        const positionTween = this.getTween(tntTransform);
        positionTween
            .to(
                {
                    x: tntTransform.position.x,
                    y: cardTransform.position.y,
                },
                500
            )
            .easing(Easing.Back.Out)
            .delay(fallOffsetIndex * 100);

        return yield* AwaitIterators([this.startTween(positionTween), this.startTween(alphaTween)]);
    }

    private *explode(
        animator: GameObject,
        cardTNT: { card: BaseCard; tnt: TNTSprite; score: number },
        initialDelay: number,
        scene: Scene
    ): IterableIterator<number> {
        yield* delay(initialDelay);

        const tntTransform = cardTNT.tnt.getComponent(TransformComponent);

        // Wiggle.
        const rotationTween = this.getTween(tntTransform);
        rotationTween.easing(Easing.Quadratic.InOut);
        yield* this.wiggle(rotationTween);

        // Hide card.
        cardTNT.card.getComponent(Renderable).visible = false;
        cardTNT.card.hideAndDestroyModifiers();

        try {
            const explosion = GameObject.Instantiate(Container);
            cardTNT.tnt.addChild(explosion);
            animator.addChild(explosion);

            StartAnimation(explosion, 'TNTExplosionEffect');
            PlaySound('tnt-individual-explosion');

            showCardScoreEffectAt(scene, animator, cardTNT.score, tntTransform.position.clone());

            this.backgroundShake();
            cardTNT.tnt.container.renderable = false;
            cardTNT.tnt.destroy();
        } catch (e) {
            console.log(e);
        }
    }

    private backgroundShake() {
        const background = GameObject.findByName('backgroundSprite')[0];
        if (background) {
            StartAnimation(background, 'LevelScreenShake', {
                amplitude: 10,
                frequency: 25,
                damping: 0.9,
            });
        }
    }

    private *wiggle(rotationTween: Tween): IterableIterator<number> {
        yield* this.rotationTo(rotationTween, Math.PI * 0.1, 75);
        yield* this.rotationTo(rotationTween, -Math.PI * 0.1, 75);
        yield* this.rotationTo(rotationTween, Math.PI * 0.1, 75);
        yield* this.rotationTo(rotationTween, -Math.PI * 0.1, 75);
        yield* this.rotationTo(rotationTween, Math.PI * 0.1, 75);
        yield* this.rotationTo(rotationTween, -Math.PI * 0.1, 75);
        yield* this.rotationTo(rotationTween, 0, 75);
    }

    private *pinch(scaleTween: Tween): IterableIterator<number> {
        yield* this.scaleTo(scaleTween, 0.8, 0.8, 125);
        yield* this.scaleTo(scaleTween, 1.2, 1.2, 125);
        yield* this.scaleTo(scaleTween, 1, 1, 125);
    }

    private *scaleTo(tween: Tween, scaleX: number, scaleY: number, delay: number): IterableIterator<number> {
        tween.to({ x: scaleX, y: scaleY }, delay);
        yield* this.startTween(tween);
    }

    private *rotationTo(tween: Tween, rotation: number, delay: number): IterableIterator<number> {
        tween.to({ rotation: rotation }, delay);
        yield* this.startTween(tween);
    }
}
